import { useState } from 'react';
import FileDownload from 'js-file-download';

import { getFormattedDate, showToast, buildLedgerQueryPathSegment, buildLedgerTypeFromOrgType } from '../../../helper';
import API, { LedgerQueryAPI } from '../../../helper/API';
import { ILedgerProvider } from '../type';
import LedgerContext, { ILedgerContext } from './LedgerContext';
import { OA_DATE_FORMAT_BE } from '../../../constants';
import moment from 'moment';
import { IPageInfo } from '../../../components/OATableComponent';
import { handleApiCall, validateOrganizationSelection, buildQueryString, updatePageInfo } from '../utils/ledgerApiHelpers';

const LedgerProvider = (props: ILedgerProvider) => {
  const { queryParams } = props?.data;
  const [ledgerList, setLedgerList] = useState<any[]>([]);
  const [internalLedgerList, setInternalLedgerList] = useState<any[]>([]);
  const [supplierTransactionsList, setSupplierTransactionsList] = useState<any[]>([]);
  const [ledgerBalance, setLedgerbalance] = useState<any>(null);
  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    totalCount: 0,
    totalPages: 0,
    count: 0,
    size: 20,
    index: 1
  });
  const getLedgerInfo = async (cb?: (response?: any) => void) => {
    await handleApiCall({
      apiCall: () => API.get(`/ledger`),
      onSuccess: (response: any) => {
        const resultData = response?.data?.data;
        cb?.(resultData);
      },
      callback: cb
    });
  };

  const getLedgerList = async (cb?: (response?: any) => void) => {
    const queryString = buildQueryString({
      dateRange: queryParams.dateRange,
      pageIndex: queryParams.pageIndex,
      dateParamNames: { start: 'start', end: 'end' },
      includePageSize: true,
      pageSize: 10
    });

    await handleApiCall({
      apiCall: () => API.get(`/ledger/entries?${queryString}`),
      onSuccess: (response: any) => {
        const resultData = response?.data?.data;
        setLedgerList(resultData?.content);
        setPageInfo(prev => updatePageInfo(prev, resultData));
      },
      callback: cb
    });
  };

  const getInternalLedgerEntryList = async (cb?: (response?: any) => void) => {
    // Validate organization selection
    const validation = validateOrganizationSelection({ requireSpecific: true, showToastOnError: true });

    if (!validation) {
      setInternalLedgerList([]);
      setPageInfo(prev =>
        Object.assign({}, prev, {
          totalCount: 0,
          totalPages: 0,
          count: 0
        })
      );
      cb?.({ error: 'No organization selected' });
      return;
    }

    // Build path segment based on organization type
    const pathSegment = buildLedgerQueryPathSegment(validation.orgData);

    if (!pathSegment) {
      showToast('Invalid organization type. Cannot load ledger entries.', 'error');
      cb?.({ error: 'Invalid organization type' });
      return;
    }

    const isSupplier = validation.orgData?.type === 'SUPPLIER';

    // For supplier type, only use page and size params (no date range)
    // For other types, include date range params
    const queryString = isSupplier
      ? buildQueryString({
          pageIndex: queryParams.pageIndex,
          includePageSize: true,
          pageSize: 10,
          additionalParams: queryParams.entryType && queryParams.entryType.trim() !== ''
            ? { entryType: queryParams.entryType }
            : {}
        })
      : buildQueryString({
          dateRange: queryParams.dateRange,
          pageIndex: queryParams.pageIndex,
          dateParamNames: { start: 'from', end: 'to' },
          includePageSize: true,
          pageSize: 10,
          additionalParams: queryParams.entryType && queryParams.entryType.trim() !== ''
            ? { entryType: queryParams.entryType }
            : {}
        });

    await handleApiCall({
      apiCall: () => LedgerQueryAPI.get(`/ledger/query/${pathSegment}?${queryString}`),
      onSuccess: (response: any) => {
        const resultData = response?.data ? response?.data : response;
        setInternalLedgerList(resultData);
        setPageInfo(prev => updatePageInfo(prev, resultData));
      },
      onError: (err: any) => {
        showToast(err?.response?.data?.message || 'Failed to load ledger entries', 'error');
      },
      callback: cb
    });
  };

  const getSupplierTransactions = async (cb?: (response?: any) => void) => {
    // Validate organization selection
    const validation = validateOrganizationSelection({ requireSpecific: false, showToastOnError: true });

    if (!validation) {
      cb?.({ error: 'Missing organization data' });
      return;
    }

    const orgCode = validation.orgCode;

    // NEW: Handle PAYOUT-only case when Onarrival is selected
    if (queryParams?.customerId === 'ONARRIVAL') {
      const payoutQueryString = buildQueryString({
        dateRange: queryParams.dateRange,
        pageIndex: queryParams.pageIndex,
        dateParamNames: { start: 'from', end: 'to' },
        includePageSize: true,
        pageSize: 10,
        additionalParams: {entryType: 'PAYOUT'} //needs static value here
      });
      await handleApiCall({
        apiCall: () => LedgerQueryAPI.get(`/ledger/query/supplier/${orgCode}/transactions?${payoutQueryString}`),
        onSuccess: (response: any) => {
          const resultData = response?.data || response;
          setSupplierTransactionsList(resultData);
          setPageInfo(prev => updatePageInfo(prev, resultData));
        },
        onError: (err: any) => {
          showToast(err?.response?.data?.message || 'Failed to load payout transactions', 'error');
        },
        callback: cb
      });
      return;
    }

    // For specific customers: fetch only that customer's transactions (no payouts)
    const queryString = buildQueryString({
      dateRange: queryParams.dateRange,
      pageIndex: queryParams.pageIndex,
      dateParamNames: { start: 'from', end: 'to' },
      includePageSize: true,
      pageSize: 10,
      additionalParams: {
        customerId: queryParams.customerId
      }
    });

    await handleApiCall({
      apiCall: () => LedgerQueryAPI.get(`/ledger/query/supplier/${orgCode}/transactions?${queryString}`),
      onSuccess: (response: any) => {
        const resultData = response?.data || response;
        setSupplierTransactionsList(resultData);
        setPageInfo(prev => updatePageInfo(prev, resultData));
      },
      onError: (err: any) => {
        showToast(err?.response?.data?.message || 'Failed to load customer transactions', 'error');
      },
      callback: cb
    });
  };

  const getCurrentBalance = async (cb?: (response?: any) => void) => {
    await handleApiCall({
      apiCall: () => API.get(`/ledger`),
      onSuccess: (response: any) => {
        const resultData = response?.data?.data;
        setLedgerbalance(resultData);
      },
      callback: cb
    });
  };

  const downloadReport = async (cb?: (response?: any) => void) => {
    const queryString = buildQueryString({
      dateRange: queryParams.dateRange,
      pageIndex: queryParams.pageIndex,
      dateParamNames: { start: 'start', end: 'end' },
      includePageSize: false
    });

    const APITemp: any = API;
    APITemp.responseType = 'blob';

    await handleApiCall({
      apiCall: () => APITemp.get(`/ledger/report?${queryString}`),
      onSuccess: (response: any) => {
        showToast('Report generated!', 'success');
        FileDownload(response?.data, 'report.csv');
      },
      onError: (err: any) => {
        showToast(err?.response?.data, 'error');
      },
      callback: cb
    });
  };

  const downloadInternalLedgerReport = async (cb?: (response?: any) => void) => {
    // Validate organization selection
    const validation = validateOrganizationSelection({ requireSpecific: false, showToastOnError: true });

    if (!validation) {
      cb?.({ error: 'Missing organization data' });
      return;
    }

    // Get ledgerType based on org type
    const ledgerType = buildLedgerTypeFromOrgType(validation.orgData?.type);
    let orgId = validation.orgCode;

    if (!orgId || !ledgerType) {
      showToast('Organization data not found. Please select an organization.', 'error');
      cb?.({ error: 'Missing organization data' });
      return;
    }

    // Handle special case for ONARRIVAL
    if (orgId === 'ONARRIVAL') {
      orgId = orgId.toLowerCase();
    }

    const queryString = buildQueryString({
      dateRange: queryParams.dateRange,
      pageIndex: queryParams.pageIndex,
      includePageSize: false,
      useISOFormat: true,
      additionalParams: {
        ledgerType,
        orgId
      }
    });

    await handleApiCall({
      apiCall: () => LedgerQueryAPI.get(`/ledger/report.csv?${queryString}`, {
        responseType: 'blob'
      }),
      onSuccess: (response: any) => {
        showToast('Report generated!', 'success');
        FileDownload(response?.data, 'internal-ledger-report.csv');
      },
      onError: (err: any) => {
        showToast(err?.response?.data || 'Failed to generate report', 'error');
      },
      callback: cb
    });
  };

  const addBalance = async (amount: number, cb?: (response?: any) => void) => {
    await handleApiCall({
      apiCall: () => API.post(`/ledger/credit?amount=${amount}`),
      onSuccess: () => {
        showToast(`INR ${amount} is added to your balance`, 'success');
      },
      onError: (err: any) => {
        showToast(err, 'error');
      },
      callback: cb
    });
  };

  const addTransaction = async (values: any, cb?: (response?: any) => void) => {
    const requestBody = getTransactionalValues(values);

    await handleApiCall({
      apiCall: () => API.post(`/ledger`, requestBody),
      onSuccess: () => {
        showToast(`Transaction added successfully`, 'success');
        getLedgerList();
        getCurrentBalance();
      },
      onError: (err: any) => {
        showToast(err?.response?.data?.errors, 'error');
      },
      callback: cb
    });
  };

  const editTransaction = async (values: any, cb?: (response?: any) => void) => {
    const requestBody = getTransactionalValues(values);

    await handleApiCall({
      apiCall: () => API.put(`/ledger/${requestBody?.id}`, requestBody),
      onSuccess: () => {
        showToast(`Transaction edited successfully`, 'success');
        getLedgerList();
        getCurrentBalance();
      },
      onError: (err: any) => {
        showToast(err?.response?.data?.errors, 'error');
      },
      callback: cb
    });
  };

  const addTopupSettlement = async (values: any, cb?: (response?: any) => void) => {
    // Validate organization selection
    const validation = validateOrganizationSelection({ requireSpecific: false, showToastOnError: true });

    if (!validation) {
      cb?.({ error: 'Missing organization data' });
      return;
    }

    const orgId = validation.orgCode || '';
    const paymentMethod = values?.paymentMethod || '';

    // Build query parameters
    const queryParamsObj = new URLSearchParams();
    queryParamsObj.append('orgId', orgId);
    if (paymentMethod) queryParamsObj.append('paymentMethod', paymentMethod);

    const queryString = queryParamsObj.toString();
    const endpoint = `/ledger/topup/settlement${queryString ? `?${queryString}` : ''}`;

    await handleApiCall({
      apiCall: () => LedgerQueryAPI.post(endpoint, values),
      onSuccess: (response: any) => {
        const resultData = response?.data;
        if (resultData?.success) {
          showToast(`Topup settlement added successfully`, 'success');
          getInternalLedgerEntryList();
        } else {
          showToast(resultData?.error);
        }
      },
      onError: (err: any) => {
        showToast(err?.response?.data?.error || 'Failed to add topup settlement', 'error');
      },
      callback: cb
    });
  };

  const addTopup = async (values: any, cb?: (response?: any) => void) => {
    // Validate organization selection
    const validation = validateOrganizationSelection({ requireSpecific: false, showToastOnError: true });

    if (!validation) {
      cb?.({ error: 'Missing organization data' });
      return;
    }

    const supplierId = validation.orgCode;

    // Add supplierId to payload
    const payload = {
      ...values,
      supplierId
    };

    await handleApiCall({
      apiCall: () => LedgerQueryAPI.post(`/ledger/payout`, payload),
      onSuccess: (response: any) => {
        const resultData = response?.data;
        if (resultData?.success) {
          showToast(`Payout added successfully`, 'success');
          getInternalLedgerEntryList();
        } else {
          showToast(`Failed to add payout`, 'error');
        }
      },
      onError: (err: any) => {
        showToast(err?.response?.data?.error || 'Failed to add payout', 'error');
      },
      callback: cb
    });
  };

  const uploadSettlementCsv = async (formData: FormData, paymentMethod?: string, cb?: (response?: any) => void) => {
    // Validate organization selection
    const validation = validateOrganizationSelection({ requireSpecific: false, showToastOnError: true });

    if (!validation) {
      cb?.({ error: 'Missing organization data' });
      return;
    }

    const orgId = validation.orgCode || '';

    // Build query parameters
    const queryParamsObj = new URLSearchParams();
    queryParamsObj.append('orgId', orgId);
    if (paymentMethod) queryParamsObj.append('paymentMethod', paymentMethod);

    const queryString = queryParamsObj.toString();
    const endpoint = `/ledger/topup/settlements/csv${queryString ? `?${queryString}` : ''}`;

    await handleApiCall({
      apiCall: () => LedgerQueryAPI.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }),
      onSuccess: async (response: any) => {
        const resultData = response?.data;

        // Check if there are failures in the response
        if (resultData?.failures && resultData.failures.length > 0) {
          const failureCount = resultData.failures.length;
          const successCount = resultData.successes?.length || 0;

          // Create detailed error message
          const errorMessages = resultData.failures.map((failure: any, index: number) => {
            return `${index + 1}. Entity: ${failure.entityId}, Settlement: ${failure.settlementId} - ${failure.error}`;
          }).join('\n');

          const summaryMessage = `CSV Upload completed with ${failureCount} failure(s) and ${successCount} success(es).\n\nFailures:\n${errorMessages}`;

          showToast(summaryMessage, 'error');
          cb?.({ ...resultData, success: false });
        } else {
          showToast(`Settlement CSV uploaded successfully. Processed: ${resultData?.processed || 0}`, 'success');
          // Refresh data and wait for completion before invoking callback
          const refreshPromises = [
            getInternalLedgerEntryList(),
            getCurrentBalance()
          ];
          // Only refresh supplier transactions if a customer is selected
          if (queryParams?.customerId) {
            refreshPromises.push(getSupplierTransactions());
          }
          await Promise.all(refreshPromises);
        }
      },
      onError: (err: any) => {
        showToast(err?.response?.data?.errors || err?.response?.data?.message || 'Failed to upload CSV', 'error');
      },
      callback: cb
    });
  };

  const contextValue: ILedgerContext = {
    getLedgerInfo,
    getLedgerList,
    getInternalLedgerEntryList,
    getSupplierTransactions,
    getCurrentBalance,
    downloadReport,
    downloadInternalLedgerReport,
    addBalance,
    addTransaction,
    editTransaction,
    addTopupSettlement,
    addTopup,
    uploadSettlementCsv,
    ledgerList,
    internalLedgerList,
    supplierTransactionsList,
    ledgerBalance,
    pageInfo
  };

  return <LedgerContext.Provider value={contextValue}>{props?.children}</LedgerContext.Provider>;
};

export default LedgerProvider;

const getTransactionalValues = (values: any) => {
  let draft: any = {
    id: values?.id ?? null,
    ledgerId: values?.ledgerId ?? null,
    orderId: values?.linkedOrderId ?? null,
    linkedOrderId: values?.orderId ?? null,
    description: values?.description,
    credit: values?.credit,
    debit: values?.debit
  };
  let transactionDate = getFormattedDate(values?.dateOfTransaction, OA_DATE_FORMAT_BE);
  let time = moment(values?.transactionTime)?.toISOString();
  draft.createdAt = `${transactionDate}T${time.split('T')[1]}`;
  return draft;
};